/* eslint-disable react/require-default-props */
import type { UserOrganization } from '@acretrader/acremaps-permissions';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { HOME_URL } from '@/constants/constantUrls';
import { MANAGE_ORGANIZATION_URL, PROFILE_URL } from '@/constants/urls';
import { changeActiveOrganization } from '@/endpoints/itasca/organization/changeActiveOrganization';
import useBreakpoints from '@/hooks/useBreakpoint';
import { useAuth } from '@/providers/auth-provider';
import { useInitializeContext } from '@/providers/InitializeProvider';
import { useModalContext } from '@/providers/ModalProvider';
import { useToastContext } from '@/providers/ToastProvider';

import Divider from '../../../components/divider';
import {
  AcresFeatureFlags,
  AUTH_ACTIONS,
  GLOBAL_TOOLTIP,
  PLAT_MAP_CONTENT_MIN_PADDING,
} from '../../../constants/constants';

interface IProps {
  isOpen: boolean;
  className?: string | null | undefined;
}

const OptionsMenu = forwardRef(({ isOpen, className }: IProps, ref) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const { getFeature } = useInitializeContext();
  const isOrgManagemenetEnabled = getFeature(
    AcresFeatureFlags.TEMP_ORG_MANAGEMENT
  );
  const { showErrorToast } = useToastContext();
  const { openAuthModal } = useModalContext();
  const { isMobile } = useBreakpoints();
  const {
    userCanSwitchOrganizations,
    userCanManageActiveOrganization,
    oryLogout,
    hasActiveSession,
    orgIdIsEqual,
    organizations,
  } = useAuth();

  const showOrgSwitcher = useMemo(() => {
    return userCanSwitchOrganizations && organizations;
  }, [userCanSwitchOrganizations, organizations]);

  const showOrgManagement = useMemo(
    () =>
      isOrgManagemenetEnabled && userCanManageActiveOrganization && !isMobile,
    [isMobile, isOrgManagemenetEnabled, userCanManageActiveOrganization]
  );

  const animateProps = useMemo(
    () => ({
      left: 'auto',
      right: 24,
      top: PLAT_MAP_CONTENT_MIN_PADDING + 48,
    }),
    []
  );

  const exitProps = useMemo(
    () => ({
      right: 24,
      top: -300,
    }),
    []
  );

  const initialProps = useMemo(
    () => ({
      bottom: 'auto',
      right: 24,
      top: -300,
    }),
    []
  );

  const onLogoutClick = useCallback(() => {
    oryLogout().then(() => {
      window.location.href = HOME_URL;
    });
  }, [oryLogout]);

  const onLoginClick = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  const onOrganizationClicked = useCallback(
    async (org: UserOrganization) => {
      if (!userCanSwitchOrganizations) {
        return;
      }

      const { res, err } = await changeActiveOrganization(org.id);

      if (err) {
        showErrorToast('Something went wrong changing organization');
      }

      if (res) {
        window.location.reload();
      }
    },
    [showErrorToast, userCanSwitchOrganizations]
  );

  return (
    <div ref={ref as any}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="options-menu"
            ref={menuRef}
            animate={animateProps}
            className={`visible absolute w-[133px] ${className}`}
            exit={exitProps}
            id="thisthing"
            initial={initialProps}
          >
            <div className="w-[144px] overflow-hidden bg-gray-10 shadow-menu">
              {/* Profile button */}
              <div className="flex flex-col gap-y-1 p-2">
                {!!hasActiveSession && (
                  <>
                    <a className="hover:no-underline" href={PROFILE_URL}>
                      <Button
                        className="w-full justify-start px-2 text-gray-90 hover:bg-gray-20"
                        size={ButtonSize.SMALL}
                        variant={ButtonVariant.GHOST}
                      >
                        <Typography variant="body2">Profile</Typography>
                      </Button>
                    </a>
                    <Divider />
                  </>
                )}

                {/* Organization section */}
                {showOrgSwitcher && (
                  <>
                    <Button
                      className="w-full justify-start px-2 text-gray-90"
                      size={ButtonSize.SMALL}
                      variant={ButtonVariant.GHOST}
                    >
                      Teams
                    </Button>

                    {organizations?.map((org) => (
                      <div
                        key={org.id}
                        data-tooltip-content={org.name || ''}
                        data-tooltip-delay-show={500}
                        data-tooltip-id={GLOBAL_TOOLTIP}
                      >
                        <Button
                          className={twMerge(
                            'w-full justify-start px-2 pl-4 text-gray-90',
                            'hover:bg-gray-20',
                            'disabled:bg-gray-30 disabled:text-gray-90 disabled:opacity-100'
                          )}
                          disabled={orgIdIsEqual(org.id)}
                          size={ButtonSize.SMALL}
                          variant={ButtonVariant.GHOST}
                          onClick={() => onOrganizationClicked(org)}
                        >
                          <Typography
                            className="truncate"
                            variant={orgIdIsEqual(org.id) ? 'button2' : 'body2'}
                          >
                            {org.name}
                          </Typography>
                        </Button>
                      </div>
                    ))}
                    <Divider />
                  </>
                )}

                {showOrgManagement && (
                  <Button
                    className="w-full justify-start px-2 text-gray-90"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                    onClick={() => router.push(MANAGE_ORGANIZATION_URL)}
                  >
                    Manage Team
                  </Button>
                )}

                {!hasActiveSession && (
                  <Button
                    className="w-full justify-start px-2 text-gray-90 hover:bg-gray-20"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                    onClick={onLoginClick}
                  >
                    <Typography variant="body2">
                      {AUTH_ACTIONS.LOGIN}
                    </Typography>
                  </Button>
                )}
                {!!hasActiveSession && (
                  <Button
                    className="w-full justify-start px-2 text-gray-90 hover:bg-gray-20"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                    onClick={onLogoutClick}
                  >
                    <Typography variant="body2">
                      {AUTH_ACTIONS.LOGOUT}
                    </Typography>
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default memo(OptionsMenu);
